/**
 * NOTIFICATION BROADCAST — compose a push, see exactly what it will look like, choose precisely who
 * gets it, and read back what actually happened.
 *
 * What this replaces: a four-field modal (audience / channel / title / body) whose audience was a
 * three-item dropdown — all retailers, all farmers, or "all sales". There was no way to send to one
 * person, no way to address a Warehouse Manager or the Accounts team at all, no picture, no link,
 * no preview, and the only feedback was "Queued 412" whether or not a single message went out.
 *
 * Three things this page refuses to guess at:
 *  • The audience count comes from the server resolving the audience with the SAME code the send
 *    uses, and it reports `reachable` separately — people with a registered device — because that
 *    is the number that will actually receive a push.
 *  • The preview renders the real payload: the image as the handset will crop it, and the deep link
 *    it will open.
 *  • The result is the provider's own counts (sent / failed / skipped), never "queued".
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import {
  Bell, ImagePlus, Link2, Loader2, Search, Send, Trash2, Save, Users, X, ChevronRight,
} from 'lucide-react';
import { api, request, fileUrl } from '../../api/client';
import {
  Button, Card, Input, Textarea, Field, Modal, Tag, Drawer, Select, Segmented, Alert,
  PageHeader, DataTable, DataTableColumn, StatusBadge, EmptyState, toast, cn,
} from '../../ui';
import { formatIst } from '../../lib/time';

// ── Types mirroring the API ─────────────────────────────────────────────────────────────────────

type Audience = 'ALL' | 'BY_ROLE' | 'BY_TERRITORY' | 'CUSTOM';

interface RoleRow { code: string; name: string; users: number; reachable: number }
interface LinkTarget {
  code: string; label: string; group: string;
  webPath: string | null; androidRoute: string | null;
  needsId?: true; idLabel?: string; roles?: string[];
}
interface RecipientRow {
  id: string; fullName: string; mobile: string; email: string; roleCode: string; reachable: boolean;
}
interface AudiencePreview {
  total: number; reachable: number;
  sample: { id: string; fullName: string; roleCode: string }[];
}
interface Campaign {
  id: string; title: string; body: string;
  imageUrl: string | null; deeplink: string | null;
  channel: string; audience: Audience;
  roleCodes: string[] | null; territoryId: string | null; userIds: string[] | null;
  status: string;
  recipientCount: number; sentCount: number; failedCount: number; skippedCount: number;
  error: string | null; sentAt: string | null; createdAt: string;
}

const AUDIENCE_TABS: { value: Audience; label: string }[] = [
  { value: 'ALL', label: 'Everyone' },
  { value: 'BY_ROLE', label: 'By role' },
  { value: 'BY_TERRITORY', label: 'By territory' },
  { value: 'CUSTOM', label: 'Specific people' },
];

const EMPTY_DRAFT = {
  id: undefined as string | undefined,
  title: '',
  body: '',
  imageUrl: '',
  deeplink: '',
  linkId: '',
  audience: 'BY_ROLE' as Audience,
  roleCodes: [] as string[],
  territoryId: '',
  userIds: [] as string[],
};
type Draft = typeof EMPTY_DRAFT;

/** The stored deeplink: a link template with `{id}` filled in, or the bare code. */
function resolveLink(code: string, id: string): string {
  if (!code) return '';
  return code.includes('{id}') ? (id ? code.replace('{id}', id.trim()) : '') : code;
}

// ── Live preview ────────────────────────────────────────────────────────────────────────────────

/**
 * How the push will look on a handset — the collapsed tray row, and the expanded card with the
 * picture. Deliberately shows BOTH: an image only appears once the notification is expanded, so a
 * preview that showed only the big version would misrepresent what most recipients see first.
 */
function NotificationPreview({ draft, link }: { draft: Draft; link: string }) {
  const title = draft.title.trim() || 'Notification title';
  const body = draft.body.trim() || 'Your message will appear here…';
  const img = draft.imageUrl ? fileUrl(draft.imageUrl) : '';
  return (
    <div className="space-y-4">
      <div>
        <p className="mb-2 type-label-strong text-content">In the notification tray</p>
        <div className="rounded-card border border-line bg-surface-2 p-3">
          <div className="flex items-start gap-2.5">
            <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-md bg-brand-600 text-white">
              <Bell size={15} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="type-caption text-muted">Green Bay Bio · now</p>
              <p className="truncate type-label-strong text-content">{title}</p>
              <p className="line-clamp-1 type-body text-content-2">{body}</p>
            </div>
            {img && <img src={img} alt="" className="size-10 shrink-0 rounded-md object-cover" />}
          </div>
        </div>
      </div>

      <div>
        <p className="mb-2 type-label-strong text-content">Expanded</p>
        <div className="overflow-hidden rounded-card border border-line bg-surface-2">
          <div className="p-3">
            <div className="flex items-start gap-2.5">
              <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-md bg-brand-600 text-white">
                <Bell size={15} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="type-caption text-muted">Green Bay Bio · now</p>
                <p className="type-label-strong text-content">{title}</p>
                <p className="whitespace-pre-wrap type-body text-content-2">{body}</p>
              </div>
            </div>
          </div>
          {img && (
            <img
              src={img}
              alt=""
              className="max-h-52 w-full object-cover"
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
          )}
          {link !== '' && (
            <div className="flex items-center gap-1.5 border-t border-line px-3 py-2 type-caption text-muted">
              <Link2 size={12} />
              <span className="truncate">Tapping opens <span className="text-content-2">{link || 'app home'}</span></span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Recipient picker ────────────────────────────────────────────────────────────────────────────

function RecipientPicker({
  selected, onChange,
}: { selected: RecipientRow[]; onChange: (v: RecipientRow[]) => void }) {
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebounced(term.trim()), 300);
    return () => clearTimeout(t);
  }, [term]);

  const { data: results = [], isFetching } = useQuery({
    queryKey: ['notif-recipients', debounced],
    queryFn: () => request<RecipientRow[]>({ url: '/notifications/recipients/search', params: { q: debounced } }),
    enabled: debounced.length >= 2,
  });

  const add = (u: RecipientRow) => {
    if (!selected.some((s) => s.id === u.id)) onChange([...selected, u]);
    setTerm('');
  };

  return (
    <div className="space-y-2.5">
      <div className="relative">
        <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
        <Input
          value={term}
          onChange={(e) => setTerm(e.currentTarget.value)}
          placeholder="Search by name, mobile or email — at least 2 characters" aria-label="Search by name, mobile or email — at least 2 characters"
          className="pl-9"
        />
        {debounced.length >= 2 && (
          <div className="absolute z-20 mt-1 max-h-60 w-full overflow-y-auto rounded-card border border-line bg-surface shadow-e1 shadow-lg">
            {isFetching && <div className="px-3 py-2 type-caption text-muted">Searching…</div>}
            {!isFetching && results.length === 0 && (
              <div className="px-3 py-2 type-caption text-muted">Nobody matches “{debounced}”.</div>
            )}
            {results.map((u) => (
              <button
                key={u.id}
                type="button"
                onClick={() => add(u)}
                className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-surface-2"
              >
                <span className="min-w-0 flex-1 truncate type-body text-content">
                  {u.fullName || '(no name)'} <span className="text-muted">· {u.mobile}</span>
                </span>
                <Tag>{u.roleCode}</Tag>
                {/* Says up front who cannot be pushed to, rather than letting them land in the skipped count. */}
                {!u.reachable && <Tag tone="warning">no device</Tag>}
              </button>
            ))}
          </div>
        )}
      </div>

      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((u) => (
            <span
              key={u.id}
              className="inline-flex items-center gap-1.5 rounded-full border border-line bg-surface-2 px-2.5 py-1 type-caption text-content"
            >
              {u.fullName || u.mobile}
              {!u.reachable && <span className="text-warning-600">· no device</span>}
              <button
                type="button"
                onClick={() => onChange(selected.filter((s) => s.id !== u.id))}
                className="text-muted hover:text-danger-600"
                aria-label={`Remove ${u.fullName}`}
              >
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────────────────────────

export function BroadcastComposerPage() {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [picked, setPicked] = useState<RecipientRow[]>([]);
  const [uploading, setUploading] = useState(false);
  const [openCampaign, setOpenCampaign] = useState<Campaign | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft((d) => ({ ...d, [k]: v }));

  const { data: roles = [] } = useQuery({
    queryKey: ['notif-audience-roles'],
    queryFn: () => request<RoleRow[]>({ url: '/notifications/audience/roles' }),
  });
  const { data: links = [] } = useQuery({
    queryKey: ['notif-links'],
    queryFn: () => request<LinkTarget[]>({ url: '/notifications/links' }),
  });
  const { data: territories = [] } = useQuery({
    queryKey: ['territories'],
    queryFn: () => request<any[]>({ url: '/territories' }),
  });
  const { data: campaigns, isLoading: loadingCampaigns } = useQuery({
    queryKey: ['notif-campaigns'],
    queryFn: () => request<{ items: Campaign[] }>({ url: '/notifications/campaigns' }),
  });

  const selectedLink = links.find((l) => l.code === draft.deeplink);
  const resolvedLink = resolveLink(draft.deeplink, draft.linkId);
  // A link template whose id is still missing would deep-link every recipient to a screen that
  // cannot load, so it blocks the send rather than going out broken.
  const linkIncomplete = !!selectedLink?.needsId && !draft.linkId.trim();

  // ── Audience preview: resolved server-side, by the same code the send will run ────────────────
  const audienceBody = useMemo(() => ({
    audience: draft.audience,
    roleCodes: draft.audience === 'BY_ROLE' ? draft.roleCodes : undefined,
    territoryId: draft.audience === 'BY_TERRITORY' ? (draft.territoryId || undefined) : undefined,
    userIds: draft.audience === 'CUSTOM' ? picked.map((p) => p.id) : undefined,
  }), [draft.audience, draft.roleCodes, draft.territoryId, picked]);

  const audienceReady =
    draft.audience === 'ALL'
    || (draft.audience === 'BY_ROLE' && draft.roleCodes.length > 0)
    || (draft.audience === 'BY_TERRITORY' && !!draft.territoryId)
    || (draft.audience === 'CUSTOM' && picked.length > 0);

  const { data: audience, isFetching: countingAudience } = useQuery({
    queryKey: ['notif-audience-preview', audienceBody],
    queryFn: () => request<AudiencePreview>({
      url: '/notifications/audience/preview', method: 'POST', data: audienceBody,
    }),
    enabled: audienceReady,
  });

  // ── Image upload ─────────────────────────────────────────────────────────────────────────────
  const onPickImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image too large — the limit is 2 MB, because FCM will not deliver a bigger one.');
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const r = await api.post('/uploads/notification-image', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = (r.data?.data ?? r.data)?.url;
      if (!url) throw new Error('The server did not return a URL');
      set('imageUrl', url);
      toast.success('Image uploaded');
    } catch (err: any) {
      toast.error(err.response?.data?.error?.message ?? err.message ?? 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  // ── Save / send ──────────────────────────────────────────────────────────────────────────────
  const payload = () => ({
    title: draft.title.trim(),
    body: draft.body.trim(),
    imageUrl: draft.imageUrl || null,
    deeplink: resolvedLink || null,
    channel: 'FCM',
    audience: draft.audience,
    roleCodes: draft.audience === 'BY_ROLE' ? draft.roleCodes : undefined,
    territoryId: draft.audience === 'BY_TERRITORY' ? draft.territoryId : undefined,
    userIds: draft.audience === 'CUSTOM' ? picked.map((p) => p.id) : undefined,
  });

  const saveDraft = useConfirmedMutation({ title: "Save this broadcast draft?", content: "Nothing is sent — it is saved so you can send it later.", okText: "Yes, save" }, {
    mutationFn: () => draft.id
      ? request<Campaign>({ url: `/notifications/campaigns/${draft.id}`, method: 'PUT', data: payload() })
      : request<Campaign>({ url: '/notifications/campaigns', method: 'POST', data: payload() }),
    onSuccess: (c) => {
      set('id', c.id);
      toast.success('Draft saved — nothing has been sent');
      qc.invalidateQueries({ queryKey: ['notif-campaigns'] });
    },
    onError: (e: any) => toast.error(e.response?.data?.error?.message ?? 'Could not save the draft'),
  });

  const sendNow = useMutation({
    mutationFn: async () => {
      // Always persist first: what went out is then a record, not a request body that vanished.
      const c = draft.id
        ? await request<Campaign>({ url: `/notifications/campaigns/${draft.id}`, method: 'PUT', data: payload() })
        : await request<Campaign>({ url: '/notifications/campaigns', method: 'POST', data: payload() });
      return request<any>({ url: `/notifications/campaigns/${c.id}/send`, method: 'POST' });
    },
    onSuccess: (r) => {
      // The provider's own counts, not "queued".
      const parts = [`${r.sent} delivered to the provider`];
      if (r.failed) parts.push(`${r.failed} failed`);
      if (r.skipped) parts.push(`${r.skipped} skipped`);
      const msg = `${parts.join(', ')} (of ${r.recipients} recipients)`;
      if (r.sent > 0) toast.success(msg);
      else toast.error(`${msg}${r.error ? ` — ${r.error}` : ''}`);
      setDraft(EMPTY_DRAFT);
      setPicked([]);
      qc.invalidateQueries({ queryKey: ['notif-campaigns'] });
    },
    onError: (e: any) => toast.error(e.response?.data?.error?.message ?? 'The broadcast failed'),
  });

  const removeCampaign = useConfirmedMutation({ title: "Delete this draft?", content: "It can’t be recovered.", okText: "Yes, delete", danger: true }, {
    mutationFn: (id: string) => request({ url: `/notifications/campaigns/${id}`, method: 'DELETE' }),
    onSuccess: () => {
      toast.success('Draft deleted');
      qc.invalidateQueries({ queryKey: ['notif-campaigns'] });
    },
    onError: (e: any) => toast.error(e.response?.data?.error?.message ?? 'Could not delete'),
  });

  const composeReady = draft.title.trim() && draft.body.trim() && audienceReady && !linkIncomplete;

  const confirmSend = () => {
    if (!composeReady) return;
    const n = audience?.reachable ?? 0;
    Modal.confirm({
      title: 'Send this notification now?',
      content: (
        <div className="space-y-2">
          <p>
            It goes to <b>{audience?.total ?? 0}</b> {audience?.total === 1 ? 'person' : 'people'}
            {n !== (audience?.total ?? 0) && (
              <> — <b>{n}</b> of whom {n === 1 ? 'has' : 'have'} a registered device and can actually receive it</>
            )}.
          </p>
          <p className="text-muted">A push cannot be recalled once it has left.</p>
        </div>
      ),
      okText: 'Send now',
      onOk: () => sendNow.mutateAsync(),
    });
  };

  const loadDraft = (c: Campaign) => {
    const match = links.find((l) => l.code === c.deeplink)
      ?? links.find((l) => l.needsId && c.deeplink?.startsWith(l.code.slice(0, l.code.indexOf('{'))));
    setDraft({
      id: c.status === 'DRAFT' ? c.id : undefined, // a sent campaign is duplicated, never re-sent
      title: c.title,
      body: c.body,
      imageUrl: c.imageUrl ?? '',
      deeplink: match?.code ?? '',
      linkId: match?.needsId && c.deeplink ? c.deeplink.slice(match.code.indexOf('{')) : '',
      audience: c.audience,
      roleCodes: c.roleCodes ?? [],
      territoryId: c.territoryId ?? '',
      userIds: c.userIds ?? [],
    });
    setPicked([]);
    setOpenCampaign(null);
    if (c.audience === 'CUSTOM') {
      toast.info('Re-pick the recipients — a saved list is not re-expanded into the picker.');
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const columns: DataTableColumn<Campaign>[] = [
    { key: 'title', title: 'Title', dataIndex: 'title', sortable: true, alwaysVisible: true },
    {
      key: 'audience', title: 'Audience', dataIndex: 'audience',
      render: (v: Audience, r) => (
        <span className="type-caption text-content-2">
          {v === 'BY_ROLE' ? (r.roleCodes ?? []).join(', ') || 'By role'
            : v === 'CUSTOM' ? `${(r.userIds ?? []).length} specific ${(r.userIds ?? []).length === 1 ? 'person' : 'people'}`
              : v === 'BY_TERRITORY' ? 'One territory'
                : 'Everyone'}
        </span>
      ),
    },
    {
      key: 'status', title: 'Status', dataIndex: 'status', sortable: true,
      render: (v: string) => <StatusBadge status={v} />,
    },
    {
      key: 'delivery', title: 'Delivery',
      render: (_: unknown, r) => r.status === 'DRAFT'
        ? <span className="type-caption text-muted">not sent</span>
        : (
          <span className="type-caption text-content-2">
            {r.sentCount} sent
            {r.failedCount > 0 && <span className="text-danger-600"> · {r.failedCount} failed</span>}
            {r.skippedCount > 0 && <span className="text-warning-600"> · {r.skippedCount} skipped</span>}
            <span className="text-muted"> of {r.recipientCount}</span>
          </span>
        ),
    },
    {
      key: 'sentAt', title: 'Sent', dataIndex: 'sentAt', sortable: true,
      render: (v: string | null) => v ? formatIst(v) : '—',
    },
    {
      key: 'actions', title: '', width: 90,
      render: (_: unknown, r) => (
        <div className="flex items-center gap-1">
          <Button size="sm" variant="ghost" onClick={() => setOpenCampaign(r)} aria-label="Open">
            <ChevronRight size={15} />
          </Button>
          {r.status === 'DRAFT' && (
            <Button size="sm" variant="ghost" onClick={() => removeCampaign.mutate(r.id)} aria-label="Delete draft">
              <Trash2 size={15} className="text-danger-600" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-5">
      <PageHeader
        title="Notification broadcast"
        subtitle="Compose a push, choose exactly who receives it, and see it before it goes."
        icon={<Bell size={18} />}
        actions={
          <div className="flex flex-wrap gap-2">
            <Button
              variant="default"
              size="sm"
              disabled={!composeReady || saveDraft.isPending}
              loading={saveDraft.isPending}
              onClick={() => saveDraft.mutate()}
            >
              <Save size={14} /> Save draft
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={!composeReady || sendNow.isPending}
              loading={sendNow.isPending}
              onClick={confirmSend}
            >
              <Send size={14} /> Send now
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        {/* ── Compose ────────────────────────────────────────────────────────────────────── */}
        <div className="space-y-5 lg:col-span-2">
          <Card>
            <p className="mb-3 type-label-strong text-content">Message</p>
            <div className="space-y-3.5">
              <Field label="Title" required>
                <Input
                  value={draft.title}
                  maxLength={200}
                  onChange={(e) => set('title', e.currentTarget.value)}
                  placeholder="e.g. Diwali scheme live from today"
                />
              </Field>
              <Field label="Message" required>
                <Textarea
                  rows={4}
                  value={draft.body}
                  maxLength={1000}
                  onChange={(e) => set('body', e.currentTarget.value)}
                  placeholder="Keep it to a couple of lines — the tray truncates the rest."
                />
              </Field>

              <Field
                label="Picture"
                hint="PNG, JPG or WebP up to 2 MB. Uploaded to this server and attached as a public link — FCM fetches the image itself, so it has to be reachable without a login."
              >
                <div className="flex flex-wrap items-center gap-2">
                  <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={onPickImage} />
                  <Button size="sm" variant="default" disabled={uploading} onClick={() => fileRef.current?.click()}>
                    {uploading ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />}
                    {draft.imageUrl ? 'Replace picture' : 'Upload picture'}
                  </Button>
                  {draft.imageUrl && (
                    <>
                      <span className="max-w-[18rem] truncate type-caption text-muted">{draft.imageUrl}</span>
                      <Button size="sm" variant="ghost" onClick={() => set('imageUrl', '')}>
                        <X size={14} /> Remove
                      </Button>
                    </>
                  )}
                </div>
              </Field>

              <Field
                label="Open when tapped"
                hint="Where the app goes when someone taps the notification. The list is the server's — every target here is a screen the apps actually have."
              >
                <div className="flex flex-wrap gap-2">
                  <Select
                    value={draft.deeplink || undefined}
                    onChange={(v) => setDraft((d) => ({ ...d, deeplink: v ?? '', linkId: '' }))}
                    allowClear
                    showSearch
                    placeholder="Nothing — just opens the app"
                    className="min-w-[16rem] flex-1"
                    options={Object.entries(
                      links.reduce<Record<string, LinkTarget[]>>((acc, l) => {
                        (acc[l.group] ??= []).push(l);
                        return acc;
                      }, {}),
                    ).map(([group, items]) => ({
                      label: group,
                      options: items.map((l) => ({ value: l.code, label: l.label })),
                    }))}
                  />
                  {selectedLink?.needsId && (
                    <Input
                      value={draft.linkId}
                      onChange={(e) => set('linkId', e.currentTarget.value)}
                      placeholder={selectedLink.idLabel ?? 'Id'}
                      className="w-40"
                      invalid={linkIncomplete}
                    />
                  )}
                </div>
                {linkIncomplete && (
                  <p className="mt-1.5 type-caption text-danger-600">
                    {selectedLink?.idLabel ?? 'An id'} is required — without it the link would open a screen that cannot load.
                  </p>
                )}
                {selectedLink && !selectedLink.androidRoute && (
                  <p className="mt-1.5 type-caption text-warning-600">
                    This screen only exists on the web. Recipients on the mobile app will land on the app home instead.
                  </p>
                )}
              </Field>
            </div>
          </Card>

          {/* ── Audience ─────────────────────────────────────────────────────────────────── */}
          <Card>
            <p className="mb-3 type-label-strong text-content">Who receives it</p>
            <Segmented
              value={draft.audience}
              onChange={(v) => setDraft((d) => ({ ...d, audience: v as Audience }))}
              options={AUDIENCE_TABS}
            />

            <div className="mt-4">
              {draft.audience === 'ALL' && (
                <p className="type-body text-content-2">
                  Every active user on the platform, in every role.
                </p>
              )}

              {draft.audience === 'BY_ROLE' && (
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {roles.map((r) => {
                    const on = draft.roleCodes.includes(r.code);
                    return (
                      <button
                        key={r.code}
                        type="button"
                        onClick={() => set(
                          'roleCodes',
                          on ? draft.roleCodes.filter((c) => c !== r.code) : [...draft.roleCodes, r.code],
                        )}
                        className={cn(
                          'rounded-card border px-3 py-2 text-left transition-colors',
                          on ? 'border-brand-600 bg-brand-50 dark:bg-brand-950' : 'border-line hover:border-brand-400',
                        )}
                      >
                        <span className={cn('block type-label-strong', on ? 'text-brand-700' : 'text-content')}>
                          {r.name}
                        </span>
                        {/* Both numbers, because "40 users" and "6 who can be pushed to" are different facts. */}
                        <span className="type-caption text-muted">
                          {r.users} active · {r.reachable} with a device
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}

              {draft.audience === 'BY_TERRITORY' && (
                <Select
                  value={draft.territoryId || undefined}
                  onChange={(v) => set('territoryId', v ?? '')}
                  showSearch
                  allowClear
                  placeholder="Pick a territory"
                  className="max-w-md"
                  options={territories.map((t: any) => ({ value: String(t.id), label: t.name ?? t.code }))}
                />
              )}

              {draft.audience === 'CUSTOM' && (
                <RecipientPicker selected={picked} onChange={setPicked} />
              )}
            </div>

            {/* Resolved server-side by the same code the send uses. */}
            {audienceReady && (
              <div className="mt-4 rounded-card border border-line bg-surface-2 px-3 py-2.5">
                {countingAudience ? (
                  <span className="inline-flex items-center gap-2 type-body text-muted">
                    <Loader2 size={14} className="animate-spin" /> Working out who that is…
                  </span>
                ) : (
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <Users size={15} className="text-brand-600" />
                    <span className="type-label-strong text-content">{audience?.total ?? 0}</span>
                    <span className="type-body text-content-2">
                      {audience?.total === 1 ? 'person' : 'people'} — of whom
                    </span>
                    <span className="type-label-strong text-content">{audience?.reachable ?? 0}</span>
                    <span className="type-body text-content-2">
                      {audience?.reachable === 1 ? 'has' : 'have'} a registered device and can receive a push
                    </span>
                    {!!audience?.sample?.length && (
                      <span className="w-full type-caption text-muted">
                        e.g. {audience.sample.map((s) => s.fullName).filter(Boolean).join(', ')}
                        {(audience.total ?? 0) > audience.sample.length && ' …'}
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}

            {audienceReady && !countingAudience && audience?.reachable === 0 && (
              <Alert
                className="mt-3"
                type="warning"
                message="Nobody in this audience has a registered device"
                description="Every recipient would be recorded as skipped. They will still see it in the in-app inbox next time they sign in."
              />
            )}
          </Card>
        </div>

        {/* ── Preview ───────────────────────────────────────────────────────────────────── */}
        <div className="lg:col-span-1">
          <Card className="lg:sticky lg:top-20">
            <p className="mb-3 type-label-strong text-content">Preview</p>
            <NotificationPreview draft={draft} link={resolvedLink} />
          </Card>
        </div>
      </div>

      {/* ── History ───────────────────────────────────────────────────────────────────────── */}
      <Card>
        <p className="mb-3 type-label-strong text-content">Sent &amp; drafted</p>
        {!loadingCampaigns && !campaigns?.items?.length ? (
          <EmptyState
            title="No broadcasts yet"
            description="Compose one above. Drafts are kept here until you send them."
          />
        ) : (
          <DataTable
            storageKey="notif-campaigns"
            rowKey="id"
            loading={loadingCampaigns}
            dataSource={campaigns?.items ?? []}
            columns={columns}
            pagination={{ pageSize: 10 }}
          />
        )}
      </Card>

      <CampaignDrawer
        campaign={openCampaign}
        onClose={() => setOpenCampaign(null)}
        onReuse={loadDraft}
      />
    </div>
  );
}

// ── Campaign detail ─────────────────────────────────────────────────────────────────────────────

function CampaignDrawer({
  campaign, onClose, onReuse,
}: { campaign: Campaign | null; onClose: () => void; onReuse: (c: Campaign) => void }) {
  const { data: recipients = [], isLoading } = useQuery({
    queryKey: ['notif-campaign-recipients', campaign?.id],
    queryFn: () => request<any[]>({ url: `/notifications/campaigns/${campaign!.id}/recipients` }),
    enabled: !!campaign && campaign.status !== 'DRAFT',
  });

  if (!campaign) return null;

  return (
    <Drawer open onClose={onClose} title={campaign.title} width={560}>
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge status={campaign.status} />
          <span className="type-caption text-muted">
            {campaign.sentAt ? formatIst(campaign.sentAt) : `Drafted ${formatIst(campaign.createdAt)}`}
          </span>
        </div>

        <p className="whitespace-pre-wrap type-body text-content-2">{campaign.body}</p>
        {campaign.imageUrl && (
          <img src={fileUrl(campaign.imageUrl)} alt="" className="max-h-52 w-full rounded-card object-cover" />
        )}
        {campaign.deeplink && (
          <p className="flex items-center gap-1.5 type-caption text-muted">
            <Link2 size={12} /> Opens <span className="text-content-2">{campaign.deeplink}</span>
          </p>
        )}

        {campaign.error && (
          <Alert type="warning" message="Not everything went out" description={campaign.error} />
        )}

        {campaign.status !== 'DRAFT' && (
          <div className="grid grid-cols-4 gap-2">
            {([
              ['Recipients', campaign.recipientCount, 'text-content'],
              ['Sent', campaign.sentCount, 'text-success-600'],
              ['Failed', campaign.failedCount, 'text-danger-600'],
              ['Skipped', campaign.skippedCount, 'text-warning-600'],
            ] as const).map(([label, n, tone]) => (
              <div key={label} className="rounded-card border border-line bg-surface-2 px-2.5 py-2">
                <p className={cn('type-label-strong', tone)}>{n}</p>
                <p className="type-caption text-muted">{label}</p>
              </div>
            ))}
          </div>
        )}

        {campaign.status !== 'DRAFT' && (
          <div>
            <p className="mb-2 type-label-strong text-content">Recipients</p>
            {isLoading && <p className="type-caption text-muted">Loading…</p>}
            <div className="max-h-72 space-y-1 overflow-y-auto">
              {recipients.map((r) => (
                <div key={r.id} className="flex items-center gap-2 rounded-md border border-line px-2.5 py-1.5">
                  <span className="min-w-0 flex-1 truncate type-caption text-content">
                    {r.fullName || `User ${r.userId}`} <span className="text-muted">· {r.roleCode}</span>
                  </span>
                  <StatusBadge status={r.status} />
                </div>
              ))}
            </div>
          </div>
        )}

        <Button variant="default" onClick={() => onReuse(campaign)} block>
          {campaign.status === 'DRAFT' ? 'Edit this draft' : 'Use as the basis for a new one'}
        </Button>
      </div>
    </Drawer>
  );
}
